import React, { useCallback, useRef } from 'react';
import { gql } from '@apollo/client';
import List from '#/common/components/List';
import type { Location } from '#/graphql/types';
import { getLocationList } from '#/graphql/queries';
import columns from '../LocationDataGrid/columns';
import { useNavigate } from 'react-router-dom';
import { Filter } from '#/common/components/DataGrid';
import useToast from '#/common/hooks/useToast';
import useLocationDelete from '../../hooks/useDeleteLocationMutation';
import { List as DxList } from 'devextreme-react/list';
import { inUseStatuses } from '../../utils/constants';

const getLocationListQuery = gql(getLocationList);

export interface LocationListProps {
  permanentFilters?: Filter[];
}

const LocationList: React.FC<LocationListProps> = (props) => {
  const { permanentFilters } = props;

  const navigate = useNavigate();

  const { confirm, alert } = useToast();
  const { deleteLocation } = useLocationDelete();
  const list = useRef<DxList | null>(null);

  const deleteItem = useCallback(
    async (items?: Location[], clearSelectedItems?: () => void) => {
      const filteredItemsForDelete = items?.filter(
        (location) =>
          location.inUseStatus === inUseStatuses.newlyCreated.key &&
          !(location.thirdPartyId && location.source),
      );

      if (!items?.length || !filteredItemsForDelete?.length) {
        alert('warning', 'No deletable locations found. Only "Newly Created" ones can be deleted.');
        return undefined;
      }

      if (filteredItemsForDelete.length < items.length) {
        const sure = await confirm(
          'error',
          `Some selected locations are not in 'Newly Created' status and can not be deleted.`,
        );
        if (!sure) {
          return;
        }
      } else {
        const sure = await confirm('error', `Are you sure you want to delete these locations?`);
        if (!sure) {
          return;
        }
      }

      await deleteLocation(items.map((item) => item.id).join(','));

      list.current?.instance && list.current.instance.reload();
      clearSelectedItems && clearSelectedItems();
    },
    [deleteLocation],
  );

  const openLocation = useCallback(
    (location: Location) => {
      navigate(`/assets/locations/${location.id}`);
    },
    [navigate],
  );

  return (
    <List<Location>
      setRef={(ref) => (list.current = ref)}
      titleKey="name"
      subtitleKey="level"
      getItemsQuery={getLocationListQuery}
      permanentFilters={permanentFilters}
      columnList={columns}
      onClick={openLocation}
      bodyFields={[
        { 
          key: 'parentId', 
          label: 'Parent Location',
          labelKey: 'parent-location' 
        },
        { 
          key: 'address', 
          label: 'Address',
          labelKey: 'address' 
        },
      ]}
      isActionDeleteOnly
      floatingButtonOnClick={deleteItem}
    />
  );
};

export default React.memo(LocationList);
